// ── ContactClaudePanel (Evo Brain — panel 2) ─────────────────────────────────
// Ya no renderiza UI propia — el shell genérico vive en AssistantDock (ver
// contexts/AssistantContext.tsx). Conserva lo propio de contacts: el toggle
// de perfil activo/inactivo, los 3 quick actions (prompts de texto, no pasan
// por el mecanismo de pills), y el link de la entidad (contacts no tiene
// ruta de detalle propia — split-pane en /contacts, sin :id — el link va a
// la lista, mismo criterio que TaskDetailPage.tsx para propuestas tipo contact).

import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button.js';
import { useAssistantModule, useRegisterAssistantActive } from '../../contexts/AssistantContext.js';
import { useContactProfile, useToggleContactProfile, type Contact } from '../../hooks/useContacts.js';

// ── Quick actions — prompts de texto, NO tools registradas — botones propios
// en topContent, igual que el toggle de perfil.
const QUICK_ACTION_PROMPTS = {
  draft_reply: 'Redacta un email profesional en español para este contacto. Usa su perfil y el historial de interacciones para personalizarlo. Devuelve el borrador en el chat, no lo envíes.',
  summarize_recent: 'Resume en 3-5 bullets concisos las últimas interacciones con este contacto. Identificá el estado actual de la relación y si hay alguna acción pendiente.',
  suggest_tone: 'Analiza el perfil e historial de este contacto. Sugerime el tono adecuado para el próximo email (formal o casual, breve o detallado, urgente o relajado) y por qué.',
} as const;

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  contact: Contact | null;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ContactClaudePanel({ contact }: Props) {
  const { t } = useTranslation();

  const contactId = contact?.id ?? null;
  const { data: profile } = useContactProfile(contactId ?? 0);
  const toggleProfile = useToggleContactProfile();

  const assistant = useAssistantModule('contact', contactId != null ? String(contactId) : null, undefined);
  const isStreaming = assistant.streaming.phase !== 'idle';

  const contactName = contact
    ? [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.businessName || `#${contact.id}`
    : null;

  const topContent = contactId != null ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)', padding: 'var(--evo-space-2) var(--evo-space-4)', borderBottom: '1px solid var(--evo-stroke)' }}>
      <Button
        variant="outline"
        tone={profile?.isActive ? 'green' : 'muted'}
        size="sm"
        fullWidth
        align="left"
        icon={<span style={{ fontSize: '10px' }}>●</span>}
        onClick={() => toggleProfile.mutate({ contactId, isActive: !(profile?.isActive ?? false) })}
      >
        {profile?.isActive ? t('contacts.profile.active') : t('contacts.profile.inactive')}
      </Button>
      <div style={{ display: 'flex', gap: 'var(--evo-space-2)' }}>
        {(Object.keys(QUICK_ACTION_PROMPTS) as Array<keyof typeof QUICK_ACTION_PROMPTS>).map(action => (
          <Button
            key={action}
            variant="outline"
            tone="cyan"
            size="xs"
            disabled={isStreaming}
            onClick={() => { void assistant.sendMessage(QUICK_ACTION_PROMPTS[action]); }}
          >
            {t(`contacts.assistant.quick.${action}`)}
          </Button>
        ))}
      </div>
    </div>
  ) : undefined;

  useRegisterAssistantActive('contact', contactId != null ? String(contactId) : null, {
    title: t('contacts.assistant.title'),
    placeholder: contactId ? t('contacts.assistant.placeholder') : t('contacts.assistant.placeholderDisabled'),
    inputDisabled: !contactId,
    emptyHint: t('contacts.assistant.hint'),
    entityLink: contactName ? { label: contactName, href: '/contacts' } : null,
    topContent,
  });

  return null;
}
